import { Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { openExternal, usePosts, useSite, useSiteSettings } from '@wordpuppi/shared';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckCircle2, Circle, ArrowRight, X } from 'lucide-react';

const key = (siteSlug: string) => `wpp:getstarted:${siteSlug}`;

/**
 * AB#673: where the "Build the site" step points. Once the site is built there
 * is something to look at, so Review opens the shareable preview instead of the
 * generation form. No preview link (older backend) -> generation page as before.
 */
export function buildStepTarget(
  done: boolean,
  previewUrl: string | null | undefined,
): { kind: 'external'; url: string } | { kind: 'route' } {
  return done && previewUrl ? { kind: 'external', url: previewUrl } : { kind: 'route' };
}

/** Turn on the first-run checklist for a freshly created site. */
export function markGetStartedActive(siteSlug: string) {
  localStorage.setItem(key(siteSlug), 'active');
}

/**
 * Dismissible first-run checklist. Renders only for sites flagged 'active' by
 * the create-site wizard and not yet dismissed — existing and dismissed sites
 * render nothing. Dismissal persists per site in localStorage.
 */
export function GetStartedChecklist({ siteSlug }: { siteSlug: string }) {
  const [status, setStatus] = useState<string | null>(() =>
    localStorage.getItem(key(siteSlug)),
  );

  // Re-read when switching sites (the posts route reuses this component).
  useEffect(() => {
    setStatus(localStorage.getItem(key(siteSlug)));
  }, [siteSlug]);

  if (status !== 'active') return null;

  return (
    <ChecklistCard
      siteSlug={siteSlug}
      onDismiss={() => {
        localStorage.setItem(key(siteSlug), 'dismissed');
        setStatus('dismissed');
      }}
    />
  );
}

type Step = {
  label: string;
  done: boolean;
  /** Set -> the action opens this URL externally instead of navigating. */
  href?: string;
} & (
  | {
      to:
        | '/sites/$siteSlug/posts/new'
        | '/sites/$siteSlug/posts'
        | '/sites/$siteSlug/settings/generation';
      params: { siteSlug: string };
    }
  | {
      to: '/sites/$siteSlug/posts/$postId';
      params: { siteSlug: string; postId: string };
    }
);

/**
 * Renders the step's action link. Narrowing on `step.to` keeps TanStack's
 * route params correctly typed (the $postId route needs postId, the others
 * don't). Rest props are spread onto the Link so Button's asChild Slot can
 * inject its className/handlers.
 */
function StepLink({
  step,
  children,
  ...rest
}: {
  step: Step;
  children: ReactNode;
} & Record<string, unknown>) {
  if (step.to === '/sites/$siteSlug/posts/$postId') {
    return (
      <Link to={step.to} params={step.params} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={step.to} params={step.params} {...rest}>
      {children}
    </Link>
  );
}

function ChecklistCard({
  siteSlug,
  onDismiss,
}: {
  siteSlug: string;
  onDismiss: () => void;
}) {
  // Completion is derived from cheap existing queries — no new backend state.
  const anyPosts = usePosts(siteSlug, { per_page: 1 });
  const published = usePosts(siteSlug, { status: 'published', per_page: 1 });
  const drafts = usePosts(siteSlug, { status: 'draft', per_page: 1 });
  const settings = useSiteSettings(siteSlug);
  const site = useSite(siteSlug);

  const hasPosts = (anyPosts.data?.meta?.total ?? 0) > 0;
  const hasPublished = (published.data?.meta?.total ?? 0) > 0;
  const hasBuilt = !!settings.data?.data?.last_built_at;

  // The publish control lives in the post editor, so send the user to an
  // existing draft's editor. If there's no draft to publish yet, land them in
  // the new-post editor rather than back on this same list page.
  const firstDraftId = drafts.data?.data?.[0]?.id;
  const publishStep: Step = firstDraftId
    ? {
        label: 'Publish it',
        done: hasPublished,
        to: '/sites/$siteSlug/posts/$postId',
        params: { siteSlug, postId: firstDraftId },
      }
    : {
        label: 'Publish it',
        done: hasPublished,
        to: '/sites/$siteSlug/posts/new',
        params: { siteSlug },
      };

  const buildTarget = buildStepTarget(hasBuilt, site.data?.data?.preview_url);

  const steps: Step[] = [
    { label: 'Create your first post', done: hasPosts, to: '/sites/$siteSlug/posts/new', params: { siteSlug } },
    publishStep,
    {
      label: 'Build the site',
      done: hasBuilt,
      to: '/sites/$siteSlug/settings/generation',
      params: { siteSlug },
      ...(buildTarget.kind === 'external' ? { href: buildTarget.url } : {}),
    },
    // Deploy has no cheap per-site signal (CF deployments are account-wide and
    // need a connection) — link only. ponytail: wire live state if the API ever
    // exposes a per-site last-deploy timestamp.
    { label: 'Deploy it', done: false, to: '/sites/$siteSlug/settings/generation', params: { siteSlug } },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Get your site live</CardTitle>
            <CardDescription>
              {doneCount} of {steps.length} done — four quick steps from empty
              site to published.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDismiss}
            aria-label="Dismiss checklist"
            className="-mr-2 -mt-1 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ol className="divide-y divide-border/60">
          {steps.map((step) => {
            const href = step.href;
            return (
            <li key={step.label} className="flex items-center gap-3 py-2.5">
              {step.done ? (
                <CheckCircle2 className="checklist-done h-5 w-5 shrink-0 text-success" />
              ) : (
                <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}
              <span
                className={cn(
                  'text-sm',
                  step.done && 'text-muted-foreground line-through',
                )}
              >
                {step.label}
              </span>
              {href ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void openExternal(href)}
                  className="ml-auto text-muted-foreground hover:text-foreground"
                >
                  Preview
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="ml-auto text-muted-foreground hover:text-foreground"
                >
                  <StepLink step={step}>
                    {step.done ? 'Review' : 'Start'}
                    <ArrowRight className="h-4 w-4" />
                  </StepLink>
                </Button>
              )}
            </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
